import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Settings, Mail, Trash2 } from "lucide-react";
import { getAuthToken } from "../../utils/authHelpers";
import "./InnerCircleAdmin.sass";

// Inner Circle roster. Unlike the password-gated admin tabs, this one calls
// an endpoint that requires a signed-in account with the admin role, because
// it exposes participant contact details and the private GroupMe invite.

const ROLE_LABELS = { host: "Host", creator: "Creator", both: "Both" };
const STATUS_LABELS = { pending: "Pending", paid: "Paid", failed: "Failed", expired: "Expired", refunded: "Refunded" };
const WELCOME_LABELS = { queued: "Queued", sending: "Sending", sent: "Sent", failed: "Failed" };
const REFERRAL_LABELS = {
    instagram: "Instagram",
    postaway_member: "Postaway member",
    referral: "Host/creator referral",
    event: "Summit/event",
    other: "Other",
};

const formatMoney = (cents, currency = "usd") => {
    if (typeof cents !== "number") return "—";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        }).format(cents / 100);
    } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
    }
};

const formatDate = (value, withTime = false) => {
    if (!value) return "—";
    return new Date(value).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
    });
};

const toLocalInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const csvEscape = (value) => {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const downloadCsv = (filename, header, rows) => {
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const propertyLine = (p) => `${p.name} (${[p.city, p.region, p.country].filter(Boolean).join(", ")})`;

const postAdmin = async (body) => {
    const token = getAuthToken();
    if (!token) return { status: 403, data: { ok: false, error: "sign_in_required" } };
    const res = await fetch("/api/admin/inner-circle", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, data };
};

const EMPTY_SETTINGS = {
    enrollmentOpen: false,
    registrationClosesAt: "",
    capacity: "",
    memberStripePriceId: "",
    regularStripePriceId: "",
    groupmeInviteUrl: "",
    meetUrl: "",
    sessionTimeLabel: "",
    timezone: "",
    supportEmail: "",
};

const settingsFromCohort = (c) => ({
    enrollmentOpen: Boolean(c.enrollmentOpen),
    registrationClosesAt: toLocalInput(c.registrationClosesAt),
    capacity: c.capacity == null ? "" : String(c.capacity),
    memberStripePriceId: c.memberStripePriceId || "",
    regularStripePriceId: c.regularStripePriceId || "",
    groupmeInviteUrl: c.groupmeInviteUrl || "",
    meetUrl: c.meetUrl || "",
    sessionTimeLabel: c.sessionTimeLabel || "",
    timezone: c.timezone || "",
    supportEmail: c.supportEmail || "",
});

const InnerCircleAdmin = () => {
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState("");
    const [loadError, setLoadError] = useState("");
    const [cohort, setCohort] = useState(null);
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");
    const [welcomeFilter, setWelcomeFilter] = useState("all");
    const [openId, setOpenId] = useState(null);
    const [actionMessage, setActionMessage] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settings, setSettings] = useState(EMPTY_SETTINGS);
    const [settingsErrors, setSettingsErrors] = useState({});
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsNotice, setSettingsNotice] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const { status, data } = await postAdmin({ action: "list" });
            if (status === 403) {
                setAuthError(
                    data.error === "not_admin"
                        ? "Your account doesn't have the admin role. Sign in with the admin account to view the Inner Circle roster."
                        : "Sign in with your admin account to view the Inner Circle roster. Your session may have expired."
                );
                return;
            }
            if (!data.ok) {
                setLoadError("Couldn't load the roster. Try refreshing.");
                return;
            }
            setAuthError("");
            setCohort(data.cohort);
            setStats(data.stats);
            setRows(data.registrations || []);
            setSettings(settingsFromCohort(data.cohort));
        } catch (_) {
            setLoadError("Couldn't load the roster. Try refreshing.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase().replace(/^@/, "");
        return rows.filter((r) => {
            if (statusFilter !== "all") {
                if (statusFilter === "failed_expired" ? !["failed", "expired"].includes(r.status) : r.status !== statusFilter) {
                    return false;
                }
            }
            if (roleFilter !== "all" && r.role !== roleFilter) return false;
            if (welcomeFilter !== "all") {
                const ws = r.welcome?.status || (r.status === "paid" ? "queued" : "none");
                if (ws !== welcomeFilter) return false;
            }
            if (!q) return true;
            return [
                r.firstName,
                r.lastName,
                `${r.firstName} ${r.lastName}`,
                r.email,
                r.primaryInstagram,
                ...(r.additionalInstagram || []),
                r.phone,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [rows, search, statusFilter, roleFilter, welcomeFilter]);

    const exportCsv = () => {
        const header = [
            "Registration ID",
            "First name",
            "Last name",
            "Role",
            "Primary Instagram",
            "Additional Instagram",
            "Email",
            "Phone",
            "Property count",
            "Properties",
            "How they heard",
            "Referral detail",
            "Tuition tier",
            "Amount",
            "Currency",
            "Status",
            "Registered",
            "Paid at",
            "Refunded at",
            "Welcome email",
            "Welcome sent at",
        ];
        const data = filtered.map((r) => [
            r.id,
            r.firstName,
            r.lastName,
            ROLE_LABELS[r.role] || r.role,
            `@${r.primaryInstagram}`,
            (r.additionalInstagram || []).map((h) => `@${h}`).join("; "),
            r.email,
            r.phone,
            r.properties.length,
            r.properties.map(propertyLine).join("; "),
            REFERRAL_LABELS[r.referralSource] || r.referralSource,
            r.referralDetail || "",
            r.priceTier === "member" ? "Member" : "Regular",
            typeof r.amountCents === "number" ? (r.amountCents / 100).toFixed(2) : "",
            (r.currency || "").toUpperCase(),
            STATUS_LABELS[r.status] || r.status,
            r.createdAt,
            r.paidAt || "",
            r.refundedAt || "",
            r.welcome ? WELCOME_LABELS[r.welcome.status] || r.welcome.status : "",
            r.welcome?.sentAt || "",
        ]);
        downloadCsv(`inner-circle-${cohort?.id || "roster"}-${new Date().toISOString().slice(0, 10)}.csv`, header, data);
    };

    const resendWelcome = async (r) => {
        setBusyId(r.id);
        setActionMessage((m) => ({ ...m, [r.id]: "" }));
        try {
            const { data } = await postAdmin({ action: "resendWelcome", registrationId: r.id });
            setActionMessage((m) => ({
                ...m,
                [r.id]: data.ok ? "Welcome email sent." : `Not sent: ${data.error || "unknown error"}`,
            }));
            await load();
        } finally {
            setBusyId(null);
        }
    };

    const deleteRegistration = async (r) => {
        const warning =
            r.status === "paid"
                ? `Delete the PAID registration for ${r.firstName} ${r.lastName}? Their payment stays in Stripe, but this roster record, their properties and email history are removed. This can't be undone.`
                : `Delete the registration for ${r.firstName} ${r.lastName}? This can't be undone.`;
        if (!window.confirm(warning)) return;
        setBusyId(r.id);
        try {
            const { data } = await postAdmin({ action: "deleteRegistration", registrationId: r.id });
            if (data.ok) {
                setOpenId(null);
                await load();
            } else {
                setActionMessage((m) => ({ ...m, [r.id]: "Delete failed." }));
            }
        } finally {
            setBusyId(null);
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSettingsSaving(true);
        setSettingsErrors({});
        setSettingsNotice("");
        try {
            const fields = {
                ...settings,
                registrationClosesAt: settings.registrationClosesAt
                    ? new Date(settings.registrationClosesAt).toISOString()
                    : "",
            };
            const { data } = await postAdmin({ action: "updateCohort", fields });
            if (data.ok) {
                setCohort(data.cohort);
                setSettings(settingsFromCohort(data.cohort));
                setSettingsNotice("Saved.");
            } else if (data.errors) {
                setSettingsErrors(data.errors);
            } else {
                setSettingsNotice("Save failed.");
            }
        } finally {
            setSettingsSaving(false);
        }
    };

    const setSetting = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

    if (authError) {
        return (
            <div className="admin-section">
                <h2>Inner Circle</h2>
                <div className="admin-error">{authError}</div>
                <p className="sa-empty">
                    <a href="/login">Sign in</a>, then come back to Admin → Inner Circle.
                </p>
            </div>
        );
    }

    const settingField = (key, label, props = {}) => (
        <label className="icad-field" key={key}>
            <span className="icad-field-label">{label}</span>
            <input
                className={`icad-input${settingsErrors[key] ? " has-error" : ""}`}
                value={settings[key]}
                onChange={(e) => setSetting(key, e.target.value)}
                {...props}
            />
            {settingsErrors[key] && <span className="icad-field-error">{settingsErrors[key]}</span>}
        </label>
    );

    return (
        <div className="admin-section icad">
            <div className="sa-header">
                <div>
                    <h2>Inner Circle</h2>
                    <p className="sa-subtitle">
                        {cohort
                            ? `${cohort.name} · Jan 20 – Apr 21, 2027 · ${formatMoney(cohort.memberPriceCents, cohort.currency)} member / ${formatMoney(cohort.regularPriceCents, cohort.currency)} regular`
                            : "Loading cohort…"}
                    </p>
                    {cohort && (
                        <span className={`sa-badge ${cohort.enrollmentOpen ? "icad-badge-paid" : "icad-badge-pending"}`}>
                            Enrollment {cohort.enrollmentOpen ? "open" : "closed"}
                        </span>
                    )}
                </div>
                <div className="sa-header-actions icad-header-actions">
                    <button type="button" className="sa-btn sa-btn-secondary" onClick={load} disabled={loading}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        type="button"
                        className="sa-btn sa-btn-secondary"
                        onClick={exportCsv}
                        disabled={loading || filtered.length === 0}
                    >
                        <Download size={14} /> Export CSV
                    </button>
                    <button
                        type="button"
                        className="sa-btn sa-btn-primary"
                        onClick={() => setSettingsOpen((v) => !v)}
                        aria-expanded={settingsOpen}
                    >
                        <Settings size={14} /> Cohort settings
                    </button>
                </div>
            </div>

            {settingsOpen && cohort && (
                <form className="icad-settings" onSubmit={saveSettings}>
                    {!cohort.configured && (
                        <p className="icad-note">
                            Add both Stripe price IDs (one-time {formatMoney(cohort.memberPriceCents, cohort.currency)} and{" "}
                            {formatMoney(cohort.regularPriceCents, cohort.currency)} prices on the Inner Circle product) and the
                            GroupMe invite before opening enrollment.
                        </p>
                    )}
                    <label className="icad-toggle">
                        <input
                            type="checkbox"
                            checked={settings.enrollmentOpen}
                            onChange={(e) => setSetting("enrollmentOpen", e.target.checked)}
                        />
                        <span>Enrollment open</span>
                    </label>
                    {settingsErrors.enrollmentOpen && <span className="icad-field-error">{settingsErrors.enrollmentOpen}</span>}
                    <div className="icad-settings-grid">
                        {settingField("registrationClosesAt", "Registration closes (optional)", { type: "datetime-local" })}
                        {settingField("capacity", "Capacity (blank = no limit)", { type: "number", min: 1, inputMode: "numeric" })}
                        {settingField("memberStripePriceId", `Member price ID (${formatMoney(cohort.memberPriceCents, cohort.currency)})`, { placeholder: "price_…" })}
                        {settingField("regularStripePriceId", `Regular price ID (${formatMoney(cohort.regularPriceCents, cohort.currency)})`, { placeholder: "price_…" })}
                        {settingField("groupmeInviteUrl", "GroupMe invite link (private)", { type: "url", placeholder: "https://groupme.com/join_group/…" })}
                        {settingField("meetUrl", "Google Meet link (optional, sent in welcome email)", { type: "url" })}
                        {settingField("sessionTimeLabel", "Session time (optional)", { placeholder: "7:00 PM" })}
                        {settingField("timezone", "Timezone (optional)", { placeholder: "Mountain Time" })}
                        {settingField("supportEmail", "Support email", { type: "email" })}
                    </div>
                    <div className="icad-settings-actions">
                        <button type="submit" className="sa-btn sa-btn-primary" disabled={settingsSaving}>
                            {settingsSaving ? "Saving…" : "Save settings"}
                        </button>
                        {settingsNotice && <span className="icad-note-inline">{settingsNotice}</span>}
                    </div>
                </form>
            )}

            {stats && (
                <div className="icad-stats">
                    <div className="sa-stat">
                        <span className="sa-stat-value">{stats.paid}</span>
                        <span className="sa-stat-label">Paid enrollments</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{formatMoney(stats.revenueCents, cohort?.currency)}</span>
                        <span className="sa-stat-label">Collected (net of refunds)</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{stats.pending}</span>
                        <span className="sa-stat-label">Pending</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{stats.failedOrExpired}</span>
                        <span className="sa-stat-label">Failed / expired</span>
                    </div>
                    <div className="sa-stat">
                        <span className="sa-stat-value">{stats.refunded}</span>
                        <span className="sa-stat-label">Refunded ({formatMoney(stats.refundedCents, cohort?.currency)})</span>
                    </div>
                    <div className="sa-stat">
                        <span className={`sa-stat-value${stats.welcomeFailed ? " icad-alert" : ""}`}>{stats.welcomeFailed}</span>
                        <span className="sa-stat-label">Welcome emails failed</span>
                    </div>
                </div>
            )}

            {stats?.duplicatePayments > 0 && (
                <div className="admin-error">
                    {stats.duplicatePayments} duplicate payment{stats.duplicatePayments === 1 ? "" : "s"} need a refund in
                    Stripe. Open the participant to see the session.
                </div>
            )}

            <div className="icad-toolbar">
                <input
                    type="search"
                    className="icad-input icad-search"
                    placeholder="Search name, email, handle or phone"
                    aria-label="Search registrations"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select className="icad-input" aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed_expired">Failed / expired</option>
                    <option value="refunded">Refunded</option>
                </select>
                <select className="icad-input" aria-label="Filter by role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="all">All roles</option>
                    <option value="host">Hosts</option>
                    <option value="creator">Creators</option>
                    <option value="both">Both</option>
                </select>
                <select className="icad-input" aria-label="Filter by welcome email" value={welcomeFilter} onChange={(e) => setWelcomeFilter(e.target.value)}>
                    <option value="all">Any welcome status</option>
                    <option value="sent">Welcome sent</option>
                    <option value="failed">Welcome failed</option>
                    <option value="queued">Welcome queued</option>
                    <option value="none">No welcome (unpaid)</option>
                </select>
            </div>

            {loadError && <div className="admin-error">{loadError}</div>}

            {loading && !rows.length ? (
                <p className="sa-empty">Loading…</p>
            ) : filtered.length === 0 ? (
                <p className="sa-empty">
                    {rows.length ? "No registrations match these filters." : "No registrations yet. They'll appear here as people sign up."}
                </p>
            ) : (
                <div className="ca-table-wrap">
                    <table className="ca-table icad-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Role</th>
                                <th className="icad-col-secondary">Instagram</th>
                                <th className="icad-col-secondary">Email</th>
                                <th className="icad-col-secondary">Phone</th>
                                <th className="icad-col-secondary">Properties</th>
                                <th className="icad-col-secondary">Tuition</th>
                                <th>Status</th>
                                <th className="icad-col-secondary">Registered</th>
                                <th className="icad-col-secondary">Welcome</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const isOpen = openId === r.id;
                                return (
                                    <React.Fragment key={r.id}>
                                        <tr className="ca-row" onClick={() => setOpenId(isOpen ? null : r.id)}>
                                            <td className="sa-cell-name">
                                                {r.firstName} {r.lastName}
                                                <div className="icad-mobile-meta">
                                                    {r.email}
                                                    <br />@{r.primaryInstagram}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`sa-badge sa-badge-${r.role === "both" ? "host" : r.role}`}>
                                                    {ROLE_LABELS[r.role] || r.role}
                                                </span>
                                            </td>
                                            <td className="icad-col-secondary">@{r.primaryInstagram}</td>
                                            <td className="icad-col-secondary">
                                                <a className="sa-email" href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()}>
                                                    {r.email}
                                                </a>
                                            </td>
                                            <td className="icad-col-secondary" style={{ whiteSpace: "nowrap" }}>{r.phone}</td>
                                            <td className="icad-col-secondary">{r.properties.length || "—"}</td>
                                            <td className="icad-col-secondary" style={{ whiteSpace: "nowrap" }}>
                                                {r.priceTier === "member" ? "Member" : "Regular"} · {formatMoney(r.amountCents, r.currency)}
                                            </td>
                                            <td>
                                                <span className={`sa-badge icad-badge-${r.status}`}>{STATUS_LABELS[r.status] || r.status}</span>
                                            </td>
                                            <td className="icad-col-secondary" style={{ whiteSpace: "nowrap" }}>{formatDate(r.createdAt)}</td>
                                            <td className="icad-col-secondary">
                                                {r.welcome ? (
                                                    <span className={`sa-badge icad-welcome-${r.welcome.status}`}>
                                                        {WELCOME_LABELS[r.welcome.status] || r.welcome.status}
                                                    </span>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                                <button
                                                    type="button"
                                                    className="icad-link-btn"
                                                    aria-expanded={isOpen}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenId(isOpen ? null : r.id);
                                                    }}
                                                >
                                                    {isOpen ? "Hide" : "View"}
                                                </button>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="icad-detail-row">
                                                <td colSpan={11}>
                                                    <div className="icad-detail">
                                                        {r.status === "refunded" && (
                                                            <div className="icad-note">
                                                                Refunded {formatDate(r.refundedAt, true)}. Remember to remove {r.firstName} from the GroupMe group.
                                                            </div>
                                                        )}
                                                        <dl className="icad-dl">
                                                            <div><dt>Email</dt><dd><a href={`mailto:${r.email}`}>{r.email}</a></dd></div>
                                                            <div><dt>Cell</dt><dd>{r.phone}</dd></div>
                                                            <div><dt>Primary Instagram</dt><dd>@{r.primaryInstagram}</dd></div>
                                                            <div>
                                                                <dt>Additional handles</dt>
                                                                <dd>{r.additionalInstagram.length ? r.additionalInstagram.map((h) => `@${h}`).join(", ") : "—"}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>Properties</dt>
                                                                <dd>
                                                                    {r.properties.length ? (
                                                                        <ul className="icad-list">
                                                                            {r.properties.map((p, i) => (
                                                                                <li key={i}>{propertyLine(p)}</li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        "—"
                                                                    )}
                                                                </dd>
                                                            </div>
                                                            <div>
                                                                <dt>Heard about us</dt>
                                                                <dd>
                                                                    {REFERRAL_LABELS[r.referralSource] || r.referralSource}
                                                                    {r.referralDetail ? `: ${r.referralDetail}` : ""}
                                                                </dd>
                                                            </div>
                                                            <div><dt>Postaway account</dt><dd>{r.userId ? "Yes" : "No account found"}</dd></div>
                                                            <div><dt>Paid at</dt><dd>{formatDate(r.paidAt, true)}</dd></div>
                                                            <div>
                                                                <dt>Welcome email</dt>
                                                                <dd>
                                                                    {r.welcome
                                                                        ? `${WELCOME_LABELS[r.welcome.status] || r.welcome.status} · ${r.welcome.attempts} attempt${r.welcome.attempts === 1 ? "" : "s"}${r.welcome.sentAt ? ` · sent ${formatDate(r.welcome.sentAt, true)}` : ""}${r.welcome.lastError ? ` · last error: ${r.welcome.lastError}` : ""}`
                                                                        : "Not queued (no verified payment)"}
                                                                </dd>
                                                            </div>
                                                        </dl>
                                                        {r.payments.length > 0 && (
                                                            <div className="icad-payments">
                                                                <div className="icad-field-label">Checkout attempts</div>
                                                                <ul className="icad-list">
                                                                    {r.payments.map((p) => (
                                                                        <li key={p.sessionId}>
                                                                            {formatDate(p.createdAt, true)} · {p.priceTier} · {formatMoney(p.amountCents, p.currency)} ·{" "}
                                                                            <strong>{p.status}</strong> · <code>{p.paymentIntentId || p.sessionId}</code>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        <div className="icad-detail-actions">
                                                            {r.status === "paid" && (
                                                                <button
                                                                    type="button"
                                                                    className="sa-btn sa-btn-secondary"
                                                                    onClick={() => resendWelcome(r)}
                                                                    disabled={busyId === r.id}
                                                                >
                                                                    <Mail size={14} /> {busyId === r.id ? "Sending…" : "Resend welcome"}
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="sa-btn sa-btn-secondary icad-danger"
                                                                onClick={() => deleteRegistration(r)}
                                                                disabled={busyId === r.id}
                                                            >
                                                                <Trash2 size={14} /> Delete
                                                            </button>
                                                            {actionMessage[r.id] && <span className="icad-note-inline">{actionMessage[r.id]}</span>}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="icad-footnote">
                CSV exports contain personal information. Store them securely and delete them when you're done. Never-paid
                registrations are deleted automatically 30 days after their last activity.
            </p>
        </div>
    );
};

export default InnerCircleAdmin;
